import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import apiClient from '@skygroup/shared/API/apiClient';
import { ApiStatus } from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import { useDailyRefresh } from '@skygroup/shared/utils/dailyRefresh';
import endpoints from '../utils/endpoints';
import { updateRates } from '../store/config';
import { refreshCartExchangeRates } from '../modules/ShoppingCart/ShoppingCartSliceV2';
import type { ratesInterface } from '../interfaces';

/**
 * Exchange rates change server-side at midnight, but the site config that
 * carries them is fetched once when the app boots. A tab left open overnight
 * would keep quoting yesterday's rates, so they are re-read shortly after the
 * browser's own local midnight.
 */

const isUsableRates = (value: any): value is ratesInterface =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0 &&
  Object.values(value).every(
    (rate) => Number.isFinite(Number(rate)) && Number(rate) > 0
  );

/**
 * Site config is the only endpoint that carries rates, so the whole payload is
 * fetched and everything except the rates is thrown away.
 *
 * It deliberately does not reuse `useAppConfig`'s `loadConfig`: that one falls
 * back to a hard-coded per-domain config when the response is not a success,
 * which at 00:02 would silently replace live rates with values compiled into
 * the bundle months ago. Here anything unexpected throws instead, leaving the
 * rates already in the store untouched.
 */
export const fetchSiteRates = async (): Promise<ratesInterface> => {
  const response = await apiClient.get(endpoints.APP.config, {
    suppressErrorToast: true,
  });

  if (response?.data?.status !== ApiStatus.success) {
    throw new Error('site config responded without success');
  }

  const rates = response?.data?.data?.siteConfig?.rates;
  if (!isUsableRates(rates)) {
    throw new Error('site config carried no usable rates');
  }
  return rates;
};

interface UseRatesMidnightRefreshOptions {
  /** Rates arrive with the bootstrap, so there is nothing to refresh before it. */
  isConfigLoaded: boolean;
  isEditMode: boolean;
  isPreviewMode: boolean;
}

const useRatesMidnightRefresh = ({
  isConfigLoaded,
  isEditMode,
  isPreviewMode,
}: UseRatesMidnightRefreshOptions) => {
  const dispatch = useDispatch();

  const apply = useCallback(
    (rates: ratesInterface) => {
      dispatch(updateRates(rates));
      // A cart built before midnight carries its own frozen snapshot of the
      // rates and prices every product through it, so refreshing only the site
      // config would leave the cart total on yesterday's conversion while the
      // header quoted today's. The reducer re-runs the totals and does nothing
      // when the cart is empty.
      dispatch(refreshCartExchangeRates(rates));
    },
    [dispatch]
  );

  return useDailyRefresh<ratesInterface>({
    // Off in the editor and in preview: that iframe is a design surface driven
    // by postMessage from the backoffice, where a background config GET has no
    // upside and could race the edits being pushed in.
    enabled: isConfigLoaded && !isEditMode && !isPreviewMode,
    fetcher: fetchSiteRates,
    apply,
    debugName: '__ratesRefresh',
  });
};

export default useRatesMidnightRefresh;
